/* «Ээлж» дэлгэц — нээх/хаах нь shell context-оор, хаагдсан ээлж дээр дарвал тайлан гарна */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cwApi, type CwShiftDto, type CwShiftReportDto } from '@/api/carwash';
import { tug } from '@/shared/format';
import { IconChevronRight, IconClock, IconLoader, IconPlay } from '@/shared/icons';
import { auth } from '@/shared/auth/storage';
import { publicRoutes } from '@/app/routes';
import { useToast } from '@/app/providers/ToastProvider';
import { useShellUi } from '@/features/shell/ShellLayout';
import { ShiftReportModal } from './ShiftDialogs';

function timeText(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function dateTimeText(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${timeText(iso)}`;
}

/* Хаагдсан ээлжүүдийн жагсаалт — мөр дээр дарвал тухайн ээлжийн тайлан гарна */
function ClosedShiftList({ onPick, loadingId }: {
  onPick: (shift: CwShiftDto) => void;
  loadingId: string | null;
}) {
  const listQ = useQuery({ queryKey: ['cw', 'shift-list'], queryFn: cwApi.shiftList });
  const list = listQ.data ?? [];
  if (!listQ.isLoading && list.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Хаагдсан ээлжүүд</div>
      <div className="flex flex-col rounded-[var(--r)] ring-1 ring-slate-100 bg-white overflow-hidden">
        {list.map((s, i) => (
          <button key={s.Id} onClick={() => onPick(s)}
            className={'flex items-center gap-3 px-3.5 py-3 text-left transition hover:bg-cyan-50/60 ' + (i % 2 ? 'bg-slate-50/60' : 'bg-white')}>
            <div className="leading-tight flex-1 min-w-0">
              <div className="text-[13px] font-bold text-slate-900">
                {dateTimeText(s.OpenedAt)} — {s.ClosedAt ? timeText(s.ClosedAt) : '—'}
              </div>
              <div className="text-[12px] text-slate-400 truncate">{s.OpenedBy ? `Касс: ${s.OpenedBy}` : ''}</div>
            </div>
            <span className="text-[12px] text-slate-400 whitespace-nowrap">{s.Cars} машин</span>
            <span className="text-[14px] font-bold text-slate-900 whitespace-nowrap">{tug(s.Revenue)}</span>
            {loadingId === s.Id
              ? <IconLoader size={15} className="animate-spin text-[var(--brand2)] shrink-0" />
              : <IconChevronRight size={16} className="text-slate-300 shrink-0" />}
          </button>
        ))}
        {listQ.isLoading && (
          <div className="px-3.5 py-4 text-[13px] text-slate-400 text-center">Ачааллаж байна…</div>
        )}
      </div>
    </div>
  );
}

export function ShiftPage() {
  const { isMobile: mobile, shiftBusy, openShift, closeShift } = useShellUi();
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [pickedReport, setPickedReport] = useState<CwShiftReportDto | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const currentQ = useQuery({
    queryKey: ['cw', 'shift'],
    queryFn: cwApi.shiftCurrent,
    refetchInterval: 30_000,
  });
  const cur = currentQ.data;
  const shift = cur?.Shift ?? null;

  const pickShift = async (s: CwShiftDto) => {
    setLoadingId(s.Id);
    try {
      setPickedReport(await cwApi.shiftReport(s.Id));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Тайлан уншигдсангүй.');
    } finally {
      setLoadingId(null);
    }
  };

  const onLogout = () => {
    auth.clear();
    qc.clear();
    nav(publicRoutes.login, { replace: true });
  };

  const items = shift && cur
    ? [
        { label: 'Ээлжийн орлого', value: tug(cur.Revenue) },
        { label: 'Угаасан машин', value: `${cur.Cars}` },
        { label: 'Дундаж чек', value: tug(cur.AvgTicket) },
      ]
    : [];

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={'mx-auto flex flex-col gap-4 ' + (mobile ? 'p-4 pb-24' : 'p-6 max-w-[560px]')}>
        <div className="flex items-baseline justify-between">
          <h1 className="text-[20px] font-extrabold text-slate-900">Ээлж</h1>
        </div>

        {currentQ.isLoading && (
          <div className="text-[13px] text-slate-400 py-8 text-center">Ачааллаж байна…</div>
        )}

        {!currentQ.isLoading && shift && cur && (
          <div className="rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-5 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><IconClock size={22} /></span>
              <div className="leading-tight">
                <div className="text-[15px] font-bold text-slate-900">Ээлж нээлттэй</div>
                <div className="text-[13px] text-slate-500">
                  {timeText(shift.OpenedAt)} цагаас{shift.OpenedBy ? ` · Касс: ${shift.OpenedBy}` : ''}
                </div>
              </div>
              <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-3 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>Идэвхтэй
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {items.map((it) => (
                <div key={it.label} className="rounded-[calc(var(--r)-2px)] bg-slate-50 ring-1 ring-slate-100 p-3">
                  <div className="text-[11px] text-slate-500">{it.label}</div>
                  <div className="text-[16px] font-extrabold text-slate-900 mt-1 tracking-tight">{it.value}</div>
                </div>
              ))}
            </div>
            <button onClick={closeShift} disabled={shiftBusy}
              className="min-h-[48px] rounded-[var(--r)] text-[15px] font-semibold text-rose-600 bg-rose-50 ring-1 ring-rose-200 hover:bg-rose-100 transition disabled:opacity-60">
              {shiftBusy ? 'Хааж байна…' : 'Ээлж хаах'}
            </button>
          </div>
        )}

        {!currentQ.isLoading && !shift && (
          <div className="rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-5 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"><IconClock size={22} /></span>
              <div className="leading-tight">
                <div className="text-[15px] font-bold text-slate-900">Ээлж хаалттай</div>
                <div className="text-[13px] text-slate-500">Машин хүлээн авахын өмнө ээлжээ нээнэ үү</div>
              </div>
            </div>
            <button onClick={openShift} disabled={shiftBusy}
              className="inline-flex items-center justify-center gap-2 min-h-[48px] rounded-[var(--r)] text-[15px] font-semibold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95 transition disabled:opacity-60">
              <IconPlay size={17} strokeWidth={2.5} />
              {shiftBusy ? 'Нээж байна…' : 'Ээлж нээх'}
            </button>
          </div>
        )}

        <ClosedShiftList onPick={(s) => void pickShift(s)} loadingId={loadingId} />

        <button onClick={onLogout}
          className="self-start text-[13px] font-semibold text-slate-400 hover:text-slate-600 transition">
          Системээс гарах
        </button>
      </div>

      <ShiftReportModal report={pickedReport} onClose={() => setPickedReport(null)} title="Ээлжийн тайлан" />
    </div>
  );
}
